import React, { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption, ComposeOption } from 'echarts';
import type {
  BarSeriesOption,
  ScatterSeriesOption,
  GaugeSeriesOption,
  BoxplotSeriesOption,
  HeatmapSeriesOption,
} from 'echarts/charts';
import type {
  GridComponentOption,
  TooltipComponentOption,
  LegendComponentOption,
  VisualMapComponentOption,
} from 'echarts/components';

import {
  SERVICE_CATEGORIES,
  CUSTOMERS,
  PRODUCTS,
  PRICING_RECORDS,
  ServiceCategory,
  filterRecordsByCategory,
  landedCostPerKg,
  grossMarginPct,
  profitDollars,
} from './mock';

// LGM theme (keep consistent with DashboardMetrics)
const LGM_ORANGE = '#F15A29';
const LGM_PURPLE = '#662D91';
const GREY = '#4A4A4A';

/** ---------- Boxplot helpers ---------- */
function quantile(sorted: number[], q: number): number {
  if (sorted.length === 0) return 0;
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  const next = sorted[base + 1];
  if (next === undefined) return sorted[base];
  return sorted[base] + rest * (next - sorted[base]);
}

function fiveNumberSummary(
  values: number[]
): [number, number, number, number, number] {
  const v = [...values].sort((a, b) => a - b);
  if (v.length === 0) return [0, 0, 0, 0, 0];
  return [
    v[0],
    quantile(v, 0.25),
    quantile(v, 0.5),
    quantile(v, 0.75),
    v[v.length - 1],
  ];
}

const clip = (s: string, n: number) =>
  s.length > n ? s.slice(0, n) + '...' : s;

type ECOption = ComposeOption<
  | BarSeriesOption
  | ScatterSeriesOption
  | GaugeSeriesOption
  | BoxplotSeriesOption
  | HeatmapSeriesOption
  | GridComponentOption
  | TooltipComponentOption
  | LegendComponentOption
  | VisualMapComponentOption
>;

type ExpandPayload = { title: string; option: EChartsOption };

const cardStyles: Record<string, React.CSSProperties> = {
  card: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    border: '1px solid #eee',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderBottom: '1px solid #f1f1f1',
    gap: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: GREY,
    lineHeight: 1.2,
  },
  sub: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
    lineHeight: 1.2,
  },
  actions: { display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 },
  iconBtn: {
    width: 34,
    height: 34,
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    background: '#fff',
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    color: GREY,
    textDecoration: 'none',
    outline: 'none',
    boxShadow: 'none',
  },
  body: { padding: '10px 12px 14px 12px' },
};

const PricingChartCard: React.FC<{
  title: string;
  subtitle?: string;
  option: EChartsOption;
  height: number;
  onExpand: (p: ExpandPayload) => void;
  renderNonce: number;
}> = ({ title, subtitle, option, height, onExpand, renderNonce }) => {
  return (
    <div style={cardStyles.card}>
      <div style={cardStyles.header}>
        <div>
          <div style={cardStyles.title}>{title}</div>
          {subtitle ? <div style={cardStyles.sub}>{subtitle}</div> : null}
        </div>
        <div style={cardStyles.actions}>
          <button
            type="button"
            style={cardStyles.iconBtn}
            aria-label="Expand"
            onClick={() => onExpand({ title, option })}
            title="Expand">
            ⛶
          </button>
        </div>
      </div>
      <div style={cardStyles.body}>
        <ReactECharts
          key={`${title}-${renderNonce}`}
          option={option}
          style={{ height: `${height}px`, width: '100%' }}
        />
      </div>
    </div>
  );
};

const Pricing: React.FC = () => {
  const [category, setCategory] = useState<ServiceCategory | 'All'>('All');
  const [selectedProductId, setSelectedProductId] = useState<string>('All');

  // Responsive layout
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Force ECharts to re-init after initial layout settles (fixes off-center first render)
  const [renderNonce, setRenderNonce] = useState(0);

  useEffect(() => {
    const t1 = window.setTimeout(() => setRenderNonce(n => n + 1), 0);
    const t2 = window.setTimeout(() => setRenderNonce(n => n + 1), 200);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setRenderNonce(n => n + 1), 60);
    return () => window.clearTimeout(t);
  }, [isDesktop, category, selectedProductId]);

  // Uses existing global modal handler created in DashboardMetrics.tsx
  const openInModal = ({ title, option }: ExpandPayload) => {
    const handler = (
      window as Window & {
        __dashboardModalHandler?: (
          payload: ExpandPayload & {
            viewMode: undefined;
            onToggleViewMode: undefined;
          }
        ) => void;
      }
    ).__dashboardModalHandler;
    if (typeof handler !== 'function') return;

    handler({
      title,
      option,
      viewMode: undefined,
      onToggleViewMode: undefined,
    });
  };

  const recordsByCategory = useMemo(
    () => filterRecordsByCategory(PRICING_RECORDS, category),
    [category]
  );

  const productOptions = useMemo(() => {
    const ids = Array.from(new Set(recordsByCategory.map(r => r.productId)));
    return ids
      .map(pid => PRODUCTS.find(p => p.id === pid))
      .filter((p): p is NonNullable<typeof p> => p != null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [recordsByCategory]);

  useEffect(() => {
    if (selectedProductId === 'All') return;
    const stillValid = recordsByCategory.some(
      r => r.productId === selectedProductId
    );
    if (!stillValid) setSelectedProductId('All');
  }, [recordsByCategory, selectedProductId]);

  const filtered = useMemo(() => {
    if (selectedProductId === 'All') return recordsByCategory;
    return recordsByCategory.filter(r => r.productId === selectedProductId);
  }, [recordsByCategory, selectedProductId]);

  const selectedRecord = useMemo(() => {
    const base = filtered.length ? filtered : recordsByCategory;
    return [...base].sort((a, b) => profitDollars(b) - profitDollars(a))[0];
  }, [filtered, recordsByCategory]);

  const selectedProduct = useMemo(
    () =>
      PRODUCTS.find((p: { id: string }) => p.id === selectedRecord.productId) ??
      PRODUCTS[0],
    [selectedRecord]
  );

  const selectedCustomer = useMemo(
    () =>
      CUSTOMERS.find(
        (c: { id: string }) => c.id === selectedRecord.customerId
      ) ?? CUSTOMERS[0],
    [selectedRecord]
  );

  const band = useMemo(() => {
    const landed = landedCostPerKg(selectedRecord);
    const floorPrice = Math.round(landed * 1.15);
    const targetLow = Math.round(landed * 1.22);
    const targetHigh = Math.round(landed * 1.3);
    const premiumHigh = Math.round(landed * 1.38);
    const actualQuote = selectedRecord.sellUnitPrice;
    const axisMax = Math.round(Math.max(premiumHigh, actualQuote) * 1.08);
    return {
      landed,
      floorPrice,
      targetLow,
      targetHigh,
      premiumHigh,
      actualQuote,
      axisMax,
    };
  }, [selectedRecord]);

  const baseText = useMemo(
    () => ({
      color: GREY,
      fontSize: 12,
      fontFamily: 'inherit' as const,
    }),
    []
  );

  /* =========================
     GRAPH 1 — RFQ PRICE BAND
     ========================= */
  const optionPriceBand = useMemo(() => {
    const floor = band.floorPrice;
    const tLow = band.targetLow;
    const tHigh = band.targetHigh;
    const prem = band.premiumHigh;

    return {
      textStyle: { fontFamily: 'inherit' },
      title: { show: false },
      grid: { left: 85, right: 20, top: 20, bottom: 85 },
      xAxis: {
        type: 'value' as const,
        min: 0,
        max: band.axisMax,
        name: `${selectedProduct.currency}/${selectedProduct.uom}`,
        nameLocation: 'middle' as const,
        nameGap: 48,
        axisLabel: baseText,
        nameTextStyle: baseText,
        splitLine: { lineStyle: { color: '#eef2f7' } },
      },
      yAxis: {
        type: 'category' as const,
        data: ['Quote'],
        axisTick: { show: false },
        axisLabel: baseText,
      },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        formatter: () =>
          [
            '<b>' + selectedProduct.name + '</b>',
            'Customer: ' + selectedCustomer.name,
            'Service Category: ' + category,
            'Landed: ' +
              band.landed.toFixed(0) +
              ' ' +
              selectedProduct.currency +
              '/' +
              selectedProduct.uom,
            'Floor: ' + floor,
            'Target: ' + tLow + '-' + tHigh,
            'Premium: ' + prem,
            'Actual Quote: ' + band.actualQuote,
          ].join('<br/>'),
      },
      legend: { bottom: 6, textStyle: baseText } as const,
      series: [
        {
          name: 'Floor',
          type: 'bar' as const,
          stack: 'range',
          barWidth: 18,
          data: [floor],
          itemStyle: { color: '#E6E6E6' },
        },
        {
          name: 'Target Low',
          type: 'bar' as const,
          stack: 'range',
          barWidth: 18,
          data: [tLow - floor],
          itemStyle: { color: LGM_ORANGE },
        },
        {
          name: 'Target High',
          type: 'bar' as const,
          stack: 'range',
          barWidth: 18,
          data: [tHigh - tLow],
          itemStyle: { color: LGM_PURPLE },
        },
        {
          name: 'Premium',
          type: 'bar' as const,
          stack: 'range',
          barWidth: 18,
          data: [prem - tHigh],
          itemStyle: { color: '#B8B8B8' },
        },
        {
          name: 'Actual Quote',
          type: 'scatter' as const,
          data: [[band.actualQuote, 'Quote']],
          symbolSize: 14,
          z: 10,
          itemStyle: { color: GREY },
        },
      ],
    } satisfies ECOption;
  }, [band, selectedProduct, selectedCustomer, category, baseText]);

  /* =========================
     GRAPH 2 — MARGIN GUARDRAIL
     ========================= */
  const optionMarginGauge = useMemo(() => {
    const gm = Math.max(
      0,
      Math.min(40, Number(grossMarginPct(selectedRecord).toFixed(1)))
    );
    const gmFloor = 18;
    const gmTarget = 24;

    return {
      textStyle: { fontFamily: 'inherit' },
      title: { show: false },
      series: [
        {
          type: 'gauge' as const,
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max: 40,
          splitNumber: 4,
          axisLine: {
            lineStyle: {
              width: 18,
              color: [
                [gmFloor / 40, '#d9534f'],
                [gmTarget / 40, '#f0ad4e'],
                [1, '#5cb85c'],
              ],
            },
          },
          pointer: { width: 6 },
          detail: { formatter: '{value}%', fontSize: 16, color: GREY },
          data: [{ value: gm, name: 'Gross Margin' }],
        },
      ],
    } satisfies ECOption;
  }, [selectedRecord]);

  /* =========================
     GRAPH 3 — PRICE WATERFALL
     ========================= */
  const optionWaterfall = useMemo(() => {
    const steps = [
      { name: 'Mfg Net Cost', value: selectedRecord.mfgUnitCost },
      { name: 'Freight/Import', value: selectedRecord.freightImport },
      { name: 'QA/Reg Overhead', value: selectedRecord.qaRegOverhead },
      { name: 'Volatility Reserve', value: selectedRecord.volatilityReserve },
      {
        name: 'Margin',
        value: Math.max(0, selectedRecord.sellUnitPrice - band.landed),
      },
    ];

    let sum = 0;
    const offsets: number[] = [];
    for (let i = 0; i < steps.length; i += 1) {
      offsets.push(sum);
      sum += steps[i].value;
    }

    return {
      textStyle: { fontFamily: 'inherit' },
      title: { show: false },
      grid: { left: 60, right: 20, top: 20, bottom: 65 },
      xAxis: {
        type: 'category' as const,
        data: [...steps.map(s => s.name), 'Sell Price'],
        axisLabel: { ...baseText, rotate: 22 },
      },
      yAxis: {
        type: 'value' as const,
        name: `USD/${selectedProduct.uom}`,
        axisLabel: baseText,
        nameTextStyle: baseText,
      },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
      },
      series: [
        {
          type: 'bar' as const,
          stack: 'wf',
          itemStyle: { color: 'transparent' },
          data: [...offsets, 0],
        },
        {
          type: 'bar' as const,
          stack: 'wf',
          itemStyle: { color: LGM_ORANGE, borderRadius: 4 },
          data: [...steps.map(s => s.value), selectedRecord.sellUnitPrice],
        },
      ],
    } satisfies ECOption;
  }, [selectedRecord, band.landed, selectedProduct.uom, baseText]);

  /* =========================
     GRAPH 4 — PRICE DISPERSION (BOXPLOT)
     ========================= */
  const optionBoxplot = useMemo(() => {
    const productIds = Array.from(
      new Set(recordsByCategory.map(r => r.productId))
    );
    const labels = productIds.map(
      pid => PRODUCTS.find(p => p.id === pid)?.name ?? pid
    );

    const boxData = productIds.map(pid => {
      const prices = recordsByCategory
        .filter(r => r.productId === pid)
        .map(r => r.sellUnitPrice);
      return fiveNumberSummary(prices);
    });

    const selectedIndex =
      selectedProductId === 'All'
        ? -1
        : productIds.findIndex(pid => pid === selectedProductId);

    return {
      textStyle: { fontFamily: 'inherit' },
      title: { show: false },
      grid: { left: 60, right: 20, top: 20, bottom: 90 },
      xAxis: {
        type: 'category' as const,
        data: labels.map(l => clip(l, 18)),
        axisLabel: { ...baseText, rotate: 35 },
      },
      yAxis: {
        type: 'value' as const,
        name: `USD/${selectedProduct.uom}`,
        axisLabel: baseText,
        nameTextStyle: baseText,
      },
      tooltip: { trigger: 'item' as const },
      series: [
        {
          type: 'boxplot' as const,
          data: boxData,
          itemStyle: { borderWidth: 1, borderColor: '#355CFF' },
        },
        ...(selectedIndex >= 0
          ? [
              {
                type: 'scatter' as const,
                name: 'Selected (Median)',
                data: [[selectedIndex, boxData[selectedIndex][2]]],
                symbolSize: 10,
                itemStyle: { color: GREY },
                z: 5,
              },
            ]
          : []),
      ],
    } satisfies ECOption;
  }, [recordsByCategory, selectedProductId, selectedProduct.uom, baseText]);

  /* =========================
     GRAPH 5 — MARGIN HEATMAP
     ========================= */
  const optionHeatmap = useMemo(() => {
    const customerIds = Array.from(
      new Set(recordsByCategory.map(r => r.customerId))
    );
    const productIds = Array.from(
      new Set(recordsByCategory.map(r => r.productId))
    );

    const customers = customerIds.map(
      cid => CUSTOMERS.find(c => c.id === cid)?.name ?? cid
    );
    const products = productIds.map(
      pid => PRODUCTS.find(p => p.id === pid)?.name ?? pid
    );

    const cIndex = new Map(customerIds.map((cid, i) => [cid, i]));
    const pIndex = new Map(productIds.map((pid, i) => [pid, i]));

    const buckets = new Map<string, { sum: number; n: number }>();
    for (let i = 0; i < recordsByCategory.length; i += 1) {
      const r = recordsByCategory[i];
      const key = r.customerId + '::' + r.productId;
      const b = buckets.get(key) ?? { sum: 0, n: 0 };
      b.sum += grossMarginPct(r);
      b.n += 1;
      buckets.set(key, b);
    }

    const heatData: Array<[number, number, number]> = [];
    buckets.forEach((b, key) => {
      const parts = key.split('::');
      const cid = parts[0];
      const pid = parts[1];
      heatData.push([
        pIndex.get(pid) ?? 0,
        cIndex.get(cid) ?? 0,
        Number((b.sum / b.n).toFixed(1)),
      ]);
    });

    return {
      textStyle: { fontFamily: 'inherit' },
      title: { show: false },
      grid: { left: 240, right: 40, top: 55, bottom: 70 },
      xAxis: {
        type: 'category' as const,
        data: products.map(p => clip(p, 20)),
        axisLabel: { ...baseText, rotate: 25 },
      },
      yAxis: {
        type: 'category' as const,
        data: customers.map(c => clip(c, 26)),
        axisLabel: baseText,
      },
      visualMap: {
        min: 0,
        max: 40,
        calculable: true,
        orient: 'horizontal' as const,
        left: 'center' as const,
        top: 8,
        inRange: { color: ['#FEE5D9', LGM_ORANGE, LGM_PURPLE] },
      },
      tooltip: {
        position: 'top',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const v = p.value as [number, number, number];
          return (
            customers[v[1]] +
            '<br/>' +
            products[v[0]] +
            '<br/>GM: ' +
            v[2] +
            '%'
          );
        },
      },
      series: [
        {
          type: 'heatmap' as const,
          data: heatData,
          emphasis: {
            itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.25)' },
          },
        },
      ],
    };
  }, [recordsByCategory, baseText]);

  const gridStyle: React.CSSProperties = isDesktop
    ? { display: 'grid', gap: 18, gridTemplateColumns: 'repeat(2, 1fr)' }
    : { display: 'grid', gap: 18, gridTemplateColumns: '1fr' };

  return (
    <div style={{ padding: 24, background: '#FAFAFA' }}>
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 14,
        }}>
        <div style={{ fontWeight: 700, color: GREY, fontSize: 18 }}>
          Pricing Dashboard
        </div>

        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
          <span style={{ fontSize: 12, color: GREY }}>Service Category</span>
          <select
            value={category}
            onChange={e =>
              setCategory(e.target.value as ServiceCategory | 'All')
            }
            style={{
              padding: '6px 10px',
              borderRadius: 999,
              border: '1px solid #d1d5db',
              background: '#fff',
            }}>
            <option value="All">All</option>
            {SERVICE_CATEGORIES.map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <span style={{ fontSize: 12, color: GREY, marginLeft: 10 }}>
            Product
          </span>
          <select
            value={selectedProductId}
            onChange={e => setSelectedProductId(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: 999,
              border: '1px solid #d1d5db',
              minWidth: 240,
              background: '#fff',
            }}>
            <option value="All">All</option>
            {productOptions.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Cards grid */}
      <div style={gridStyle}>
        <PricingChartCard
          title="Price Band"
          subtitle={selectedProduct.name + ' | ' + selectedCustomer.name}
          option={optionPriceBand}
          height={320}
          onExpand={openInModal}
          renderNonce={renderNonce}
        />

        <PricingChartCard
          title="Margin Guardrail"
          subtitle={selectedProduct.name}
          option={optionMarginGauge}
          height={300}
          onExpand={openInModal}
          renderNonce={renderNonce}
        />

        <PricingChartCard
          title="Price Waterfall"
          subtitle={selectedProduct.name}
          option={optionWaterfall}
          height={340}
          onExpand={openInModal}
          renderNonce={renderNonce}
        />

        <PricingChartCard
          title="Price Dispersion"
          subtitle={'by Product (' + category + ')'}
          option={optionBoxplot}
          height={340}
          onExpand={openInModal}
          renderNonce={renderNonce}
        />

        {/* Full-width on desktop */}
        <div style={isDesktop ? { gridColumn: '1 / -1' } : undefined}>
          <PricingChartCard
            title="Margin Heatmap"
            subtitle={'Customer × Product (' + category + ')'}
            option={optionHeatmap as EChartsOption}
            height={420}
            onExpand={openInModal}
            renderNonce={renderNonce}
          />
        </div>
      </div>
    </div>
  );
};

export default Pricing;
